import axios from 'axios';
import config from './apiConfig';

const createChatGPTRequest = (messages, model, maxTokens) => {
  const systemMessage = messages.find(msg => msg.role === 'assistant')?.content || '';
  const userMessages = messages.filter(msg => msg.role === 'user');
  const userPrompt = userMessages.map(msg => msg.content).join(' ');

  return {
    prompt: userPrompt,
    systemPrompt: systemMessage,
    requestType: 2,
    model: model || config.DEFAULTS.model,
    temperature: 1,
    topP: 0.7,
    topK: 5,
    maxTokens: maxTokens || config.DEFAULTS.maxTokens
  };
};

export class APIService {
  constructor(baseUrl = config.BACKEND_URL) {
    this.baseUrl = baseUrl;
  }

  async createCompletion({ messages, model, maxTokens }) {
    try {
      const completionRequest = createChatGPTRequest(messages, model, maxTokens);

      const response = await axios.post(
        `${this.baseUrl}${config.ENDPOINTS.COMPLETION}`,
        completionRequest,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response?.data?.data?.result) {
        try {
          const parsedResult = JSON.parse(response.data.data.result);
          const content =
            Array.isArray(parsedResult.content)
              ? parsedResult.content[0]?.text || ''
              : parsedResult.content || '';
          return { content };
        } catch (jsonErr) {
          return { content: response.data.data.result };
        }
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          throw new Error(
            `API Error: ${error.response.status} - ${error.response.data?.message || 'Unknown error'}`
          );
        } else if (error.request) {
          throw new Error('No response received from the API. Please check your internet connection.');
        } else {
          throw new Error(`Error setting up the request: ${error.message}`);
        }
      }
      throw error;
    }
  }

  async streamCompletion({ messages, model, maxTokens, anthropicVersion, anthropicBeta, onText, onEnd, onError }) {
    try {
      const systemMessage = messages.find(msg => msg.role === 'assistant')?.content || '';
      const userMessages = messages.filter(msg => msg.role === 'user');

      const response = await fetch(`${config.BACKEND_URL}${config.ENDPOINTS.STREAM}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          systemMessage,
          messages: userMessages,
          model: model || config.DEFAULTS.model,
          maxTokens: maxTokens || config.DEFAULTS.maxTokens,
          anthropicVersion: anthropicVersion || config.DEFAULTS.anthropicVersion,
          anthropicBeta: anthropicBeta || config.DEFAULTS.anthropicBeta
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { value, done } = await reader.read();
          
          if (done) {
            onEnd?.();
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          let startIndex = 0;
          let endIndex;

          while ((endIndex = buffer.indexOf('\n', startIndex)) !== -1) {
            const line = buffer.slice(startIndex, endIndex).trim();
            startIndex = endIndex + 1;

            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              if (data === '[DONE]' || data === '[[{{end-of-data-stream-from-the-source}}]]') {
                onEnd?.();
                return;
              } else {
                try {
                  const parsedData = JSON.parse(data);
                  if (parsedData.text) {
                    onText?.(parsedData.text);
                  }
                } catch (parseError) {
                  console.warn('Failed to parse streaming response line:', line, parseError);
                }
              }
            }
          }

          buffer = buffer.slice(startIndex);
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Streaming error:', error);
      onError?.(error);
    }
  }
}

export const apiService = new APIService();

export const streamAnthropicMessages = async ({ 
  messages, 
  model = config.DEFAULTS.model, 
  maxTokens = config.DEFAULTS.maxTokens, 
  onText, 
  onEnd, 
  onError,
}) => {
  return apiService.streamCompletion({
    messages,
    model,
    maxTokens,
    onText,
    onEnd,
    onError,
  });
};

const anthropicCompat = {
  messages: {
    create: async ({ messages, model = config.DEFAULTS.model, max_tokens = config.DEFAULTS.maxTokens }) => {
      const response = await apiService.createCompletion({
        messages,
        model,
        maxTokens: max_tokens,
      });
      
      return {
        content: [{ text: response.content || response.text || response.message }]
      };
    }
  }
};

export default anthropicCompat; 